import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const formatDisplayName = (
  firstName: string | null,
  lastNameInitial: string | null | undefined
): string => {
  const name = firstName ?? "";
  return lastNameInitial ? `${name} ${lastNameInitial}.` : name;
};

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

export const formatBytes = (bytes: number): string => {
  const exponent = Math.min(
    Math.floor(Math.log2(Math.max(bytes, 1)) / 10),
    BYTE_UNITS.length - 1
  );

  return `${(bytes / 1024 ** exponent).toLocaleString("de-DE", {
    maximumFractionDigits: exponent ? 1 : 0,
  })} ${BYTE_UNITS[exponent]}`;
};

const relativeTimeFormatter = new Intl.RelativeTimeFormat("de-DE", {
  numeric: "always",
});

export const formatLastActivity = (
  lastActivityAt: Date | null,
  now: Date = new Date()
): string => {
  if (!lastActivityAt) return "Noch nicht aktiv";

  const diffMs = now.getTime() - lastActivityAt.getTime();
  if (diffMs < 5 * 60 * 1000) return "Jetzt aktiv";

  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60)
    return `Zuletzt ${relativeTimeFormatter.format(-diffMin, "minute")}`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24)
    return `Zuletzt ${relativeTimeFormatter.format(-diffHours, "hour")}`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7)
    return `Zuletzt ${relativeTimeFormatter.format(-diffDays, "day")}`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5)
    return `Zuletzt ${relativeTimeFormatter.format(-diffWeeks, "week")}`;

  return formatInactiveSince(lastActivityAt);
};

export const formatInactiveSince = (date: Date): string => {
  const weekday = new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
  }).format(date);
  const time = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${weekday}, ${time}`;
};
